import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';

import { cn } from '../lib/cn';

/**
 * Generic confirm-with-reason dialog.  Used wherever an admin action
 * requires free-text justification (≥ minLength chars) — ban, transfer
 * ownership, soft delete, impersonate, etc.
 *
 * onConfirm receives the trimmed reason.  Returns `false` from onConfirm
 * (or throws) to keep the dialog open.
 */
export interface ReasonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  /** Confirm button label. */
  confirmLabel: string;
  /** Tone of the confirm button — danger uses red, default uses orange. */
  tone?: 'default' | 'danger';
  /** Minimum reason length.  Server enforces ≥10 chars on every admin mutation
   *  endpoint (post-Phase-1-safety-floor); keep this in sync if you change one. */
  minLength?: number;
  /** Pending state — disables the form + shows spinner text. */
  pending?: boolean;
  /** Called with the trimmed reason on confirm.  Async — dialog stays open until resolved. */
  onConfirm: (reason: string) => Promise<unknown> | unknown;
  /** Optional extra slot rendered above the reason input (e.g. plan dropdown for grant). */
  children?: React.ReactNode;
  /** Label above the textarea (default "Reason"). */
  reasonLabel?: string;
  placeholder?: string;
}

export function ReasonDialog(props: ReasonDialogProps) {
  const [reason, setReason] = useState('');
  const minLength = props.minLength ?? 10;

  useEffect(() => {
    if (!props.open) setReason('');
  }, [props.open]);

  const trimmed = reason.trim();
  // An optional note is still validated server-side (≥10 chars when present).
  const lengthOk = minLength > 0 ? trimmed.length >= minLength : trimmed.length === 0 || trimmed.length >= 10;
  const canSubmit = lengthOk && !props.pending;

  async function submit() {
    if (!canSubmit) return;
    try {
      await props.onConfirm(trimmed);
    } catch {
      // Caller toast'ed already via MutationCache.  Keep dialog open
      // so the operator can adjust reason / retry.
    }
  }

  return (
    <Dialog.Root open={props.open} onOpenChange={props.onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-xl bg-neutral-900 p-5 shadow-2xl ring-1 ring-neutral-800 sm:p-6"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <div className="mb-4 flex items-start justify-between">
            <div>
              <Dialog.Title className="text-base font-semibold text-neutral-100">{props.title}</Dialog.Title>
              <Dialog.Description className="mt-1 text-sm text-neutral-400">
                {props.description}
              </Dialog.Description>
            </div>
            <Dialog.Close className="rounded p-1 text-neutral-500 hover:bg-neutral-800 hover:text-neutral-300">
              <X size={16} />
            </Dialog.Close>
          </div>

          {props.children ? <div className="mb-4">{props.children}</div> : null}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            <label htmlFor="reason-dialog-text" className="mb-1.5 block text-xs uppercase tracking-wider text-neutral-400">
              {props.reasonLabel ?? 'Reason'}{' '}
              <span className="normal-case tracking-normal text-neutral-600">
                {minLength > 0 ? `(≥ ${minLength} chars; recorded in audit log)` : '(optional; recorded in audit log)'}
              </span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              id="reason-dialog-text"
              rows={3}
              autoFocus
              required={minLength > 0}
              placeholder={props.placeholder ?? 'Briefly explain why.'}
              onKeyDown={(e) => {
                // ⌘/Ctrl+Enter submits — keyboard-only operators never need the mouse.
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  void submit();
                }
              }}
              className="w-full resize-none rounded-md bg-neutral-800 px-3 py-2 text-base sm:text-sm text-neutral-100 outline-none ring-1 ring-neutral-700 focus:ring-2 focus:ring-orange-500"
            />
            <div className="mt-1 text-xs text-neutral-500">
              {minLength > 0
                ? `${trimmed.length}/${minLength}+ characters · ⌘/Ctrl+Enter to confirm`
                : lengthOk
                  ? '⌘/Ctrl+Enter to confirm'
                  : 'A note needs at least 10 characters (or leave it empty).'}
            </div>

            <div className="mt-5 flex items-center justify-end gap-2">
              <Dialog.Close className="rounded-md px-3 py-1.5 text-sm text-neutral-400 hover:bg-neutral-800 hover:text-neutral-200">
                Cancel
              </Dialog.Close>
              <button
                type="submit"
                disabled={!canSubmit}
                className={cn(
                  'rounded-md px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50',
                  props.tone === 'danger'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-orange-600 hover:bg-orange-700',
                )}
              >
                {props.pending ? 'Working…' : props.confirmLabel}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
